/**
 * Thin wrapper over the Weblytics tracker.
 *
 * The tracker tag in public/index.html is `async`, so it is very often still in
 * flight when the app wants to talk to it — the router fires its first
 * afterEach before the bundle has finished downloading, and the Catalyst user
 * lookup can resolve just as early. Calls made before then are queued on
 * window.Weblytics.q and replayed by the tracker once it boots, so nothing has
 * to wait for it and nothing is lost.
 *
 * Every export here is safe to call at any time, including when the tracker
 * never loads at all (blocked, offline, ad blocker) — the queue simply grows
 * and is never drained, which is the correct outcome for analytics.
 */

function call(method, ...args) {
   const w = window.Weblytics;
   // `__ps` is the tracker's own "I am ready" marker; until it is set, queue.
   if (w && w.__ps) w[method](...args);
   else (window.Weblytics = w || { q: [] }).q.push([method, args]);
}

/**
 * Attach identity to the current visitor.
 *
 * @param {{userId: string}} user
 */
export const identify = (user) => call('identify', user);

/**
 * Name the current page by its ROUTE PATTERN rather than its address, so every
 * visit to a solution collapses into one page in the reports instead of one
 * page per id.
 *
 * @param {string} pattern e.g. '/solutions/:ProjectId'
 */
export const setRoute = (pattern) => call('setRoute', pattern);

/**
 * Identify the signed-in Catalyst user.
 *
 * Takes the `content` object from getCurrentProjectUser() — the shape Catalyst
 * returns, carrying first_name, last_name and email_id.
 *
 * userId is the only field sent, formatted "Name - email", so the reports show
 * who someone is and which account it was in a single value.
 *
 * Joining with filter(Boolean) rather than concatenating: a user with no email,
 * or no name, then reads as just the part that exists instead of "Kishore P - "
 * or " - kishore.p@zappyworks.com" with a dangling separator.
 *
 * Silently does nothing when signed out. While the AUTH_CONTROL RemoveLogin
 * switch is on the site is browsable anonymously and this lookup is expected to
 * fail, so an unidentified visitor is a normal state, not an error.
 *
 * @param {object} [content] the Catalyst current-project-user content
 * @returns {boolean} whether an identity was sent
 */
export function identifyCatalystUser(content) {
   if (!content) return false;

   const name = [content.first_name, content.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
   const email = (content.email_id || '').trim();

   const userId = [name, email].filter(Boolean).join(' - ');

   // Nothing worth sending: an identify with an empty userId would create an
   // anonymous bucket that every unidentified visitor falls into.
   if (!userId) return false;

   identify({ userId });
   return true;
}
